/**
 * Inclusive-tax pricing (RP-1955).
 *
 * The vendor types the price the customer pays: the menu price. For an
 * inclusive-tax item the stored `price` is the pre-tax base in whole cents and
 * the typed price is stored next to it as `menu_price`. The base is the floor
 * of menu / (1 + rate) and the remainder is tax, the rule the tablets already
 * use for inclusive modifiers. No whole-cent base reaches every menu price
 * ($15.00 at 19.375% gives $15.01 or $14.99), so the menu price must be stored
 * and the tablets charge it as typed.
 *
 * Rates are fractions (0.19375 for 19.375%).
 */
const SCALE = 1e8;

/** Pre-tax base in whole cents for an inclusive item: floor(menu / (1 + rate)), exact in integer arithmetic. */
export const inclusiveBaseCents = (menuCents: number, rate: number): number => {
    const cents = Math.round(Number(menuCents) || 0);
    const r = Number(rate) || 0;
    if (cents <= 0) return 0;
    if (r <= 0) return cents;
    return Math.floor((cents * SCALE) / (SCALE + Math.round(r * SCALE)));
};

/** The tax part of an inclusive menu price in cents. */
export const inclusiveTaxCents = (menuCents: number, rate: number): number =>
    Math.max(Math.round(Number(menuCents) || 0) - inclusiveBaseCents(menuCents, rate), 0);

export interface PricedItem {
    price?: number | null;
    menu_price?: number | null;
    tax_percentage?: number | null;
    taxable_item?: boolean | null;
    tax_type?: string | null;
}

/**
 * The menu price of an item in cents: the stored `menu_price` when present,
 * else the stored base for an exclusive item, else base plus rounded tax
 * (the legacy derivation for inclusive items saved before `menu_price` existed).
 */
export const menuPriceCentsOf = (item: PricedItem): number => {
    const base = Math.round(Number(item.price) || 0);
    const isExclusive =
        item.taxable_item === true || (item.taxable_item == null && item.tax_type === "exclusive");
    if (isExclusive) return base;
    const stored = Number(item.menu_price);
    if (Number.isFinite(stored) && stored > 0) return Math.round(stored);
    return Math.round(base * (1 + (Number(item.tax_percentage) || 0)));
};
